/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { Skeleton, Spin, Tooltip } from 'antd';
import { CSSProperties, useCallback, useEffect, useMemo, useRef } from 'react';
import { Suspense } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { hideSentItemsAtom } from 'packages/omnitag/state';
import styled from '@emotion/styled';
import { css } from '@emotion/css';
import { ResultMeta } from 'packages/omnitag/results/result-meta';
import { MinusSquareOutlined, PlusSquareOutlined } from '@ant-design/icons';
import { Roadmap } from '@carbon/icons-react';
import classNames from 'classnames';
import { ResultStatusIcon } from 'packages/omnitag/results/result-status-icon';
import { ThumbnailImageAndLink } from 'packages/omnitag/results/thumbnail-image-and-link';
import { useJobStatusAPI } from 'packages/lib/api/logminer/fetch-hooks';
import { LoadingThumbnail } from 'packages/omnitag/results/results-layout';
import {
  addSentItemKeyAtom,
  getOmnitagResultJobStatusKey,
} from 'packages/omnitag/state/job-status';
import { OmnitagResultItem } from 'packages/lib/api/omnitag/contract/omnitag-search';
import {
  toggleSelectedResultTransaction,
  selectedResultsAtom,
} from 'packages/omnitag/state/selected-results';
import { purple } from '@ant-design/colors';
import { customDefaultTheme } from 'packages/lib/common-ui/theme';
import { useRecoilValue } from 'recoil';
import { uiThemeAtom } from 'packages/at-shell/state/recoil/ui';
import { useInView } from 'packages/omnitag/hooks/use-in-view';
import {
  debugOverrideOverlapAtom,
  overlapDurationAtom,
  overlapHzAtom,
  overlapPipelineTokenAtom,
  overlapStatusFamily,
  overlapSettingsVersionAtom, // Import the new atom
} from 'packages/omnitag/state/overlap';
import { checkOverlap } from 'packages/omnitag/api/check-overlap';
import { ConsolidatedErrorIcon } from 'packages/omnitag/results/consolidated-error-icon';

const MOTIONAL_PURPLE = customDefaultTheme.token?.colorPrimary ?? purple[5];

const styles: { [name: string]: CSSProperties } = {
  thumbnailImage: {
    width: '240px',
    height: '158px',
    objectFit: 'contain',
  },
};

export const thumbnailContainerClass = css`
  min-width: 240px;
  aspect-ratio: 240/158;
  background: rgba(190, 190, 190, 0.2);
  border-radius: 0.25rem;
  /* margin: 0 1rem 1rem 0; */
  position: relative;
  overflow: hidden;
  .ant-skeleton-element .ant-skeleton-image {
    width: 240px;
    aspect-ratio: 240/158;
    height: auto;
  }
`;

const BoundImage = css`
  max-height: 100vh;
  place-self: center;
`;
const ImageContainer = styled.div`
  cursor: pointer;
  container-type: inline-size;
  display: grid;
  height: 100%;
  :not(:root):fullscreen {
    grid-template-columns: 1fr 400px;
  }
  &:not(:fullscreen):hover .annotated-image {
    filter: url(#blurFilter);
  }
`;

const selectedClassDark = css`
  outline: 5px solid ${MOTIONAL_PURPLE};
`;
const selectedClassLight = css`
  outline: 5px solid ${'#9f9fffff'};
`;

const iconButtonTopRight = css`
  position: absolute;
  right: 0;
  top: 0;
  z-index: 10;
  font-size: 1.5rem;
  &:hover {
    opacity: 0.5;
  }
  &:active {
    opacity: 0.75;
  }
`;

const selectionIcon = css`
  display: none;
  color: #b3ecff;
  .image-container:hover & {
    display: block;
  }
  *:not(:root):fullscreen & {
    display: none;
  }
`;

const overlapOverlay = css`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  z-index: 5;
  transition: opacity 0.3s ease;
  pointer-events: none; /* Let clicks pass through to the image */

  .image-container:hover & {
    opacity: 0;
  }
`;

const overlapIcon = css`
  position: absolute;
  top: 5px;
  left: 5px;
  z-index: 6;
  color: white;
`;

const overlapSpinner = css`
  position: absolute;
  bottom: 5px;
  right: 5px;
  z-index: 6;
  background-color: rgba(0, 0, 0, 0.8);
  border-radius: 50%;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export function ResultItem({
  result,
}: {
  result: OmnitagResultItem;
}): JSX.Element | null {
  const {
    jobStatus,
    isLoading: isJobStatusLoading,
    error: jobStatusError,
  } = useJobStatusAPI(result.log_name, result.timestamp.toString());
  const hideSentItems = useAtomValue(hideSentItemsAtom);
  const selectedSet = useAtomValue(selectedResultsAtom);
  const theme = useRecoilValue(uiThemeAtom);
  const isSelected =
    result && Array.from(selectedSet).some((r) => r.id === result.id);
  const toggleSelectedResult = useSetAtom(toggleSelectedResultTransaction);

  const sceneId = `${result.log_name}-${result.timestamp}`;
  const [overlapStatus, setOverlapStatus] = useAtom(
    overlapStatusFamily(sceneId)
  );
  const duration = useAtomValue(overlapDurationAtom);
  const hz = useAtomValue(overlapHzAtom);
  const pipelineToken = useAtomValue(overlapPipelineTokenAtom);
  const debugOverride = useAtomValue(debugOverrideOverlapAtom);
  const overlapSettingsVersion = useAtomValue(overlapSettingsVersionAtom);
  const { ref, isInView } = useInView({ threshold: 0.1 });
  const isInitialMount = useRef(true);

  // This effect resets the overlap status to 'idle' whenever the global overlap settings change.
  useEffect(() => {
    // We don't want to run this on the initial mount, only on subsequent changes to the version.
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setOverlapStatus('idle');
  }, [overlapSettingsVersion, setOverlapStatus]);

  const errors = useMemo(() => {
    const errorList: { id: string; message: string; copyText: string }[] = [];
    if (jobStatusError) {
      errorList.push({
        id: 'jobStatus',
        message: "Failed to load 'Sent to Annotation' status.",
        copyText: `Failed to get job status for item: ${result.log_name} at ${
          result.timestamp
        }. Full error: ${JSON.stringify(jobStatusError, null, 2)}`,
      });
    }
    if (overlapStatus === 'error') {
      errorList.push({
        id: 'overlap',
        message:
          'Failed to perform an overlap check for this item due to a backend error, please report this error.',
        copyText: `Overlap check failed for item: ${result.log_name} at ${
          result.timestamp
        }. Parameters used: Duration=${duration}s, Hz=${hz}, PipelineToken=${
          pipelineToken ?? 'None'
        }. Please report this issue.`,
      });
    }
    return errorList;
  }, [
    jobStatusError,
    overlapStatus,
    result.log_name,
    result.timestamp,
    duration,
    hz,
    pipelineToken,
  ]);

  // This effect performs the overlap check when the item is in view and its status is 'idle'.
  useEffect(() => {
    const performCheck = async () => {
      setOverlapStatus('loading');
      try {
        const overlapMap = await checkOverlap(
          [{ log_name: result.log_name, timestamp: result.timestamp }],
          duration,
          hz,
          pipelineToken
        );
        const status = overlapMap.get(sceneId) ?? false;
        setOverlapStatus(status);
      } catch {
        setOverlapStatus('error');
      }
    };

    if (debugOverride !== 'off') {
      if (debugOverride === 'loading') setOverlapStatus('loading');
      else if (debugOverride === 'is_overlapping') setOverlapStatus(true);
      else if (debugOverride === 'error') setOverlapStatus('error');
      return;
    }

    if (pipelineToken === null) {
      setOverlapStatus(false); // No pipeline specified, so no overlap detection
      return;
    }

    if (isInView && overlapStatus === 'idle') {
      void performCheck();
    }
  }, [
    isInView,
    overlapStatus,
    setOverlapStatus,
    sceneId,
    result.log_name,
    result.timestamp,
    duration,
    hz,
    pipelineToken,
    debugOverride,
  ]);

  const resultJobStatusKey = getOmnitagResultJobStatusKey(
    result.log_name,
    result.timestamp
  );

  // This is so filtering can be done that excludes 'sent' items
  const addSentItemKey = useSetAtom(addSentItemKeyAtom);
  useEffect(() => {
    if (!isJobStatusLoading && jobStatus) {
      addSentItemKey(resultJobStatusKey);
    }
  }, [jobStatus, isJobStatusLoading, addSentItemKey, resultJobStatusKey]);

  const toggleSelected = useCallback(
    (e: React.MouseEvent<HTMLSpanElement, MouseEvent>): void => {
      e.stopPropagation();

      toggleSelectedResult(result);
    },
    [result, toggleSelectedResult]
  );

  if (hideSentItems && isJobStatusLoading) return <LoadingThumbnail />;

  return (
    <div
      ref={ref}
      data-testid="omnitag-result-item"
      className={classNames(thumbnailContainerClass, {
        [selectedClassLight]:
          isSelected && !isJobStatusLoading && theme === 'light',
        [selectedClassDark]:
          isSelected && !isJobStatusLoading && theme === 'dark',
      })}
    >
      <div style={styles.thumbnailImage}>
        <Suspense fallback={<Skeleton.Image active />}>
          <ImageContainer className="image-container">
            {overlapStatus === true && (
              <>
                <div className={overlapOverlay} />
                <div className={overlapIcon}>
                  <Tooltip title="Overlaps with an existing scene">
                    <Roadmap size={24} />
                  </Tooltip>
                </div>
              </>
            )}
            {overlapStatus === 'loading' && (
              <div className={overlapSpinner}>
                <Tooltip title="Checking for overlap...">
                  <Spin size="small" />
                </Tooltip>
              </div>
            )}
            <ConsolidatedErrorIcon errors={errors} />
            {isSelected ? (
              <MinusSquareOutlined
                className={classNames(iconButtonTopRight, selectionIcon)}
                onClick={toggleSelected}
              />
            ) : (
              <PlusSquareOutlined
                data-testid="omnitag-result-item-select-button"
                className={classNames(iconButtonTopRight, selectionIcon)}
                onClick={toggleSelected}
              />
            )}
            <ThumbnailImageAndLink
              jobStatus={jobStatus}
              className={BoundImage}
              result={result}
              isOverlapping={overlapStatus === true}
            />
            <ResultStatusIcon
              jobStatus={jobStatus}
              isLoading={isJobStatusLoading}
            />
            <ResultMeta result={result} />
          </ImageContainer>
        </Suspense>
      </div>
    </div>
  );
}
